/**
 * The 54×54 counter ring the Publish Readiness card wears (design-system.md §6
 * "Progress ring"; `.progress-ring` in the prototype's contest-overview.css).
 *
 * The ring is drawn as a stroked circle whose dash pattern is one full
 * circumference, so the visible arc is the part not pushed out by
 * `stroke-dashoffset`. That is also what makes it animate: the offset is the one
 * property that changes, and it transitions over 0.6s, so the arc sweeps rather
 * than jumps when a checklist item is ticked.
 *
 * The `-90deg` rotation starts the arc at twelve o'clock; SVG would otherwise
 * begin it at three.
 */
import Box from '@mui/material/Box';
import type { Theme } from '@mui/material/styles';

/** Geometry from the prototype: a 54-unit box, ring radius 22, stroke 5. */
const SIZE = 54;
const RADIUS = 22;
const STROKE = 5;

/**
 * Exported so the tests can state the expected arc in terms of the ring rather
 * than restating 2πr, which would just be the implementation typed twice.
 */
export const RING_CIRCUMFERENCE = 2 * Math.PI * RADIUS;

interface ProgressRingProps {
  value: number;
  total: number;
  /** Names the ring for assistive tech, e.g. "Publish readiness". */
  label: string;
}

export const ProgressRing = ({ value, total, label }: ProgressRingProps) => {
  // Clamped to the total it is drawn against. Two lists that briefly disagree
  // can put `value` outside 0..total, and an unclamped ratio gives a negative
  // dash offset (the arc drawn backwards) or one larger than the dash array,
  // plus an `aria-valuenow` outside its own declared min/max.
  const shown = Math.min(Math.max(value, 0), Math.max(total, 0));
  // An empty checklist is `0/0`, which is complete by one reading and undefined
  // by another. Drawing it empty is the honest one: there is no progress to show.
  const completed = total === 0 ? 0 : shown / total;

  return (
    <Box
      role="progressbar"
      aria-label={label}
      aria-valuenow={shown}
      aria-valuemin={0}
      // Floored at 1. An empty checklist is `0/0`, and `aria-valuemax="0"` is a
      // degenerate range that assistive tech reports inconsistently — some read
      // it as 100% complete. `aria-valuetext` below carries the honest wording
      // either way.
      aria-valuemax={Math.max(total, 1)}
      aria-valuetext={`${shown} of ${total} complete`}
      sx={{ position: 'relative', width: `${SIZE}px`, height: `${SIZE}px`, flexShrink: 0 }}
    >
      <Box
        aria-hidden
        component="svg"
        viewBox={`0 0 ${SIZE} ${SIZE}`}
        sx={{ width: '100%', height: '100%', transform: 'rotate(-90deg)' }}
      >
        <Box
          component="circle"
          cx={SIZE / 2}
          cy={SIZE / 2}
          r={RADIUS}
          sx={(theme: Theme) => ({
            fill: 'none',
            stroke: theme.tokens.componentColor.ringTrack,
            strokeWidth: STROKE,
          })}
        />
        <Box
          component="circle"
          cx={SIZE / 2}
          cy={SIZE / 2}
          r={RADIUS}
          strokeDasharray={RING_CIRCUMFERENCE}
          strokeDashoffset={RING_CIRCUMFERENCE * (1 - completed)}
          sx={(theme: Theme) => ({
            fill: 'none',
            stroke: theme.tokens.color.accent,
            strokeWidth: STROKE,
            strokeLinecap: 'round',
            transition: 'stroke-dashoffset 0.6s ease',
          })}
        />
      </Box>
      <Box
        aria-hidden
        sx={(theme: Theme) => ({
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: theme.tokens.font.mono,
          fontSize: '12px',
          fontWeight: 600,
          letterSpacing: '-0.02em',
          color: theme.tokens.color.ink2,
          fontVariantNumeric: 'tabular-nums',
          // The label outgrows its 44px of inner diameter at three digits a side;
          // let it overflow the ring rather than wrap inside it.
          whiteSpace: 'nowrap',
        })}
      >
        {/* `shown`, not `value` — the clamp exists because two lists that briefly
            disagree can overshoot, and printing the raw figure would put "7/5" on
            screen while the arc is full and the ring announces "5 of 5". */}
        {shown}/{total}
      </Box>
    </Box>
  );
};
